import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { green, common } from "@/theme/colors";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_MIN_CHARS = 3;

/**
 * Reusable scoped search input shared by the navbar (cross-entity, manual
 * submit) and index pages (single-scope, instant in-memory filtering). The
 * matching itself lives in the shared `searchSite` engine on the consumer side;
 * this component only owns the input UI plus the mode/minChars trigger logic so
 * there is a single search input across the app.
 */
export default function SearchBar({
  scope = "all",
  mode,
  minChars = DEFAULT_MIN_CHARS,
  onSearch,
  value: controlledValue,
  onValueChange,
  placeholder = "Search Site...",
  sx,
  showButton = true,
  clearable = true,
  onFocus,
  onBlur
}) {
  const [internalValue, setInternalValue] = useState("");
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const setValue = next => {
    if (onValueChange) onValueChange(next);
    if (controlledValue === undefined) setInternalValue(next);
  };

  // Instant mode: report the gated query whenever it changes (including on
  // mount / external value restores). Below minChars we report "" so the
  // consumer clears its filter. No network call originates here.
  const trimmed = value.trim();
  const effective = trimmed.length >= minChars ? trimmed : "";
  const lastReportedRef = useRef(null);
  useEffect(() => {
    if (mode !== "instant") return;
    if (lastReportedRef.current === effective) return;
    lastReportedRef.current = effective;
    onSearch?.(effective, scope);
    // onSearch is intentionally excluded — consumers often pass an inline fn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective, mode, scope]);
  const submit = () => {
    onSearch?.(trimmed, scope);
  };
  if (mode === "instant") {
    return /*#__PURE__*/_jsx(TextField, {
      size: "small",
      placeholder: placeholder,
      value: value,
      onChange: e => setValue(e.target.value),
      onFocus: onFocus,
      onBlur: onBlur,
      slotProps: {
        input: {
          endAdornment: clearable && value ? /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            onClick: () => setValue(""),
            edge: "end",
            "aria-label": "Clear search",
            children: "\u2715"
          }) : null
        }
      },
      sx: {
        minWidth: 220,
        flex: "1 1 220px",
        ...sx
      }
    });
  }

  // manual (navbar) variant — Autocomplete + green submit button.
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      ...sx
    },
    children: [/*#__PURE__*/_jsx(Autocomplete, {
      freeSolo: true,
      options: [],
      value: value,
      onChange: (_e, v) => setValue(v || ""),
      onInputChange: (_e, v) => setValue(v),
      ListboxProps: {
        style: {
          maxHeight: 260,
          overflowY: "auto"
        }
      },
      sx: {
        flex: "0 0 auto",
        width: {
          xs: "150px",
          sm: "180px",
          md: "200px",
          lg: "clamp(150px, 25vw, 600px)"
        },
        minWidth: "150px"
      },
      renderInput: params => /*#__PURE__*/_jsx(TextField, {
        ...params,
        size: "small",
        placeholder: placeholder,
        onFocus: onFocus,
        onBlur: onBlur,
        onKeyDown: e => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        },
        InputProps: {
          ...params.InputProps,
          startAdornment: /*#__PURE__*/_jsx(SearchIcon, {
            sx: {
              color: "text.secondary",
              mr: 0.5
            }
          })
        }
      })
    }), showButton && /*#__PURE__*/_jsx(IconButton, {
      onClick: submit,
      "aria-label": "Search site",
      sx: {
        backgroundColor: green.primary,
        color: common.white,
        borderRadius: 1,
        "&:hover": {
          backgroundColor: green.light
        }
      },
      children: /*#__PURE__*/_jsx(SearchIcon, {})
    })]
  });
}